import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/contexts/AuthContext';
import { empresaVisivelNoMarketplace } from '@/lib/marketplace';
import { useSolicitacoesEmpresa } from '@/hooks/useSolicitacoes';
import { getFotoSolicitacaoUrl } from '@/services/solicitacoes.service';
import { formatarMoeda } from '@/lib/currency';
import { EnviarOrcamentoDialog } from '@/components/solicitacoes/EnviarOrcamentoDialog';

function FotoMini({ path }: { path: string }) {
  const { data: url } = useQuery({ queryKey: ['solicitacao-foto', path], queryFn: () => getFotoSolicitacaoUrl(path), staleTime: 50 * 60 * 1000 });
  if (!url) return <div className="h-16 w-16 rounded bg-muted animate-pulse" />;
  return <img src={url} alt="" className="h-16 w-16 rounded object-cover" />;
}

export default function Solicitacoes() {
  const { t } = useTranslation();
  const { organization } = useAuthContext();
  const elegivel = empresaVisivelNoMarketplace(organization?.plano ?? null, organization?.subscription_status ?? null);
  const { data: itens, isLoading } = useSolicitacoesEmpresa();
  const [dialogFor, setDialogFor] = useState<string | null>(null);

  return (
    <MainLayout>
      <div className="p-6">
        <PageHeader titulo={t('solicitacoes.title')} />
      </div>
      <div className="px-6 pb-6">
        {!elegivel ? (
          <div className="flex flex-col items-center text-center text-muted-foreground py-16 gap-3">
            <Megaphone className="h-10 w-10" />
            <p>{t('solicitacoes.gate')}</p>
            <Link to="/configuracoes/plano" className="underline">{t('solicitacoes.gateCta')}</Link>
          </div>
        ) : isLoading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}</div>
        ) : !itens || itens.length === 0 ? (
          <div className="flex flex-col items-center text-center text-muted-foreground py-16 gap-3">
            <Megaphone className="h-10 w-10" />
            <p>{t('solicitacoes.empty')}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">{t('solicitacoes.subtitle')}</p>
            <ul className="space-y-3">
              {itens.map((s) => (
                <li key={s.solicitacao_id} className="rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">{s.categoria.nome} · {s.cidade}</span>
                    <span className="text-xs text-muted-foreground">{s.primeiro_nome}</span>
                  </div>
                  <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{s.descricao}</p>
                  {s.fotos.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">{s.fotos.map((p) => <FotoMini key={p} path={p} />)}</div>
                  )}
                  <div className="mt-3">
                    {s.ja_orcei && s.meu_orcamento ? (
                      <span className="text-sm text-muted-foreground">
                        {t('solicitacoes.alreadyQuoted', { value: formatarMoeda(s.meu_orcamento.valor) })}
                      </span>
                    ) : (
                      <Button size="sm" onClick={() => setDialogFor(s.solicitacao_id)}>{t('solicitacoes.sendQuote')}</Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <EnviarOrcamentoDialog solicitacaoId={dialogFor} open={dialogFor !== null} onOpenChange={(o) => !o && setDialogFor(null)} />
    </MainLayout>
  );
}
